/* Pixieset comparison — the integration argument as narrative. */

import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { GOLD, GOLD_LIGHT } from "./constants";

export default function PixiesetComparisonSection() {
  const tools = [
    { name: "Pixieset Website", price: "+$9/mo" },
    { name: "Pixieset Studio Manager", price: "+$25/mo" },
    { name: "Pixieset Store", price: "+rev share" },
    { name: "+ Calendly", price: "+$10/mo" },
    { name: "+ HoneyBook", price: "+$39/mo" },
    { name: "+ Stripe / Square", price: "+fees" },
  ];

  return (
    <section
      className="border-t border-white/[0.07] py-28"
      data-testid="pixieset-comparison"
    >
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
        <Reveal>
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">
              Why one weave beats four add-ons
            </div>
            <h2
              className="text-4xl md:text-5xl font-light text-stone-100 leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Pixieset is{" "}
              <em className="italic text-stone-400">four products</em>.
              <br />
              Lumetier is{" "}
              <em className="italic" style={{ color: GOLD }}>one.</em>
            </h2>
            <div className="mt-8 space-y-5 text-stone-300 leading-relaxed">
              <p>
                On Pixieset, your website is a separate $9/mo add-on. Studio Manager is another product with its own data. The Store is a third. None of them know about your contracts — so you bolt on HoneyBook. None of them book sessions — so you bolt on Calendly. Six tools later you&apos;re back to a spreadsheet on Friday night.
              </p>
              <p className="text-stone-100">
                <strong className="font-medium">In Lumetier, your website knows when a session was booked.</strong>{" "}
                Your gallery knows the client&apos;s contract. Your manager knows what was downloaded and what&apos;s still owed. One database. One login. One feel.
              </p>
              <p>
                The integration isn&apos;t a marketing claim — it&apos;s the entire product. We made the things that talk to each other live in the same place from day one. That&apos;s the wedge.
              </p>
            </div>
            <a
              href="#apply"
              className="inline-flex items-center gap-2 mt-9 px-6 h-11 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 transition-opacity"
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
              data-testid="px-cta"
            >
              Move my studio over <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="space-y-5">
            <div className="bg-white/[0.025] border border-white/[0.08] p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-4">
                Your stack today
              </div>
              <div className="space-y-2">
                {tools.map((t) => (
                  <div
                    key={t.name}
                    className="flex items-center justify-between text-sm py-2 border-b border-white/[0.06] last:border-0"
                  >
                    <span className="text-stone-300">{t.name}</span>
                    <span className="text-stone-500 text-xs font-mono">{t.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/[0.12] flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-stone-500">Monthly</span>
                <span className="text-lg text-stone-100 font-mono">~$83+ /mo</span>
              </div>
            </div>

            <div className="text-center text-[10px] uppercase tracking-[0.32em] text-stone-600 py-1">
              ↓ collapses into ↓
            </div>

            <div
              className="p-6 border"
              style={{
                background: `linear-gradient(135deg, ${GOLD}1a, ${GOLD}06)`,
                borderColor: `${GOLD}50`,
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: GOLD }}>
                Your stack with Lumetier
              </div>
              <div
                className="text-3xl font-light text-stone-100"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                Lumetier
              </div>
              <div className="text-sm text-stone-300 mt-1.5">
                Website · Galleries · Bookings · Contracts · Payments · Reviews
              </div>
              <div className="mt-4 pt-4 border-t border-white/[0.15] flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-stone-400">Monthly</span>
                <span className="text-lg text-stone-100 font-mono">$29–69 /mo</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
